import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createTournament, updateTournament } from '../../store/slices/tournamentsSlice';
import { fetchWinningPatterns } from '../../store/slices/winningPatternsSlice';
import { RootState } from '../../store';
import { Tournament } from '../../types';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const schema = yup.object().shape({
  name: yup.string().required('Tournament name is required'),
  stake: yup.number().positive('Stake must be positive').required('Stake is required'),
  maxPlayers: yup.number().positive('Max players must be positive').required('Max players is required'),
  scheduledStartTime: yup.string().required('Start time is required'),
  winningPattern: yup.string().required('Winning pattern is required'),
});

type FormData = {
  name: string;
  stake: number;
  maxPlayers: number;
  scheduledStartTime: string;
  winningPattern: string;
};

interface TournamentFormModalProps {
  tournament?: Tournament | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TournamentFormModal: React.FC<TournamentFormModalProps> = ({
  tournament,
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  // Get winning patterns from Redux store
  const { patterns: winningPatterns } = useSelector((state: RootState) => state.winningPatterns);

  // Get user from localStorage
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  };

  const currentUser = getCurrentUser();
  const isEditing = !!tournament;

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: tournament?.name || '',
      stake: tournament?.stake || 0,
      maxPlayers: tournament?.maxPlayers || 0,
      scheduledStartTime: tournament?.scheduledStartTime ? dayjs(tournament.scheduledStartTime).format('YYYY-MM-DDTHH:mm') : '',
      winningPattern: tournament?.winningPattern || '',
    },
  });

  const watchedWinningPattern = watch('winningPattern');

  React.useEffect(() => {
    if (open) {
      reset({
        name: tournament?.name || '',
        stake: tournament?.stake || 0,
        maxPlayers: tournament?.maxPlayers || 0,
        scheduledStartTime: tournament?.scheduledStartTime ? dayjs(tournament.scheduledStartTime).format('YYYY-MM-DDTHH:mm') : '',
        winningPattern: tournament?.winningPattern || '',
      });
      // Fetch winning patterns when modal opens
      dispatch(fetchWinningPatterns({ page: 1, limit: 100 }) as any);
    }
  }, [open, tournament, reset, dispatch]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const tournamentData = {
        ...data,
        scheduledStartTime: dayjs(data.scheduledStartTime).toISOString(),
      };

      if (isEditing && tournament) {
        const result = await dispatch(updateTournament({ tournamentId: tournament.id, tournamentData }) as any);
        if (result.payload) {
          toast.success('Tournament updated successfully');
          onSuccess();
          onClose();
        }
      } else {
        const result = await dispatch(createTournament(tournamentData) as any);
        if (result.payload) {
          toast.success('Tournament created successfully');
          onSuccess();
          onClose();
        } else {
          toast.error(result.error?.message || 'Failed to create tournament');
        }
      }
    } catch (error) {
      toast.error(isEditing ? 'Failed to update tournament' : 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {isEditing ? 'Edit Tournament' : 'Create Tournament'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'column', md: 'row' }} gap={{ xs: 2, sm: 3 }}>
            <Box flex={1}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Tournament Name"
                    placeholder="Weekend Bingo Championship"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name="stake"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Stake Amount"
                    type="number"
                    placeholder="50"
                    error={!!errors.stake}
                    helperText={errors.stake?.message}
                    InputProps={{
                      startAdornment: 'ETB ',
                    }}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name="maxPlayers"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Maximum Players"
                    type="number"
                    placeholder="20"
                    error={!!errors.maxPlayers}
                    helperText={errors.maxPlayers?.message}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name="scheduledStartTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Scheduled Start Time"
                    type="datetime-local"
                    error={!!errors.scheduledStartTime}
                    helperText={errors.scheduledStartTime?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name="winningPattern"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.winningPattern}>
                    <InputLabel>Winning Pattern</InputLabel>
                    <Select {...field} label="Winning Pattern">
                      {winningPatterns.map((pattern) => (
                        <MenuItem key={pattern.id} value={pattern.id}>
                          {pattern.name} - {pattern.description}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.winningPattern && (
                      <Typography variant="caption" color="error">
                        {errors.winningPattern.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          </Box>
          {currentUser && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Tournament will be hosted by: {currentUser.username || currentUser.id}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Tournament' : 'Create Tournament')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TournamentFormModal; 